import { memo } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { useUIStore } from '../../store/uiStore'
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import { cn } from '../../lib/utils'

interface SaveIndicatorProps {
  className?: string
}

export const SaveIndicator = memo(({ className }: SaveIndicatorProps) => {
  const { saveStatus, lastSaved, showSaveIndicator } = useUIStore()

  if (!showSaveIndicator) {
    return null
  }

  const formatLastSaved = () => {
    if (!lastSaved) return null
    try {
      return formatDistanceToNow(lastSaved, { addSuffix: true })
    } catch {
      return null
    }
  }

  const getSaveStatusDisplay = () => {
    switch (saveStatus) {
      case 'saving':
        return {
          icon: <Loader2 className="w-3 h-3 animate-spin" />,
          text: 'Saving...',
          className: 'text-blue-600'
        }
      case 'saved':
        return {
          icon: <CheckCircle className="w-3 h-3" />,
          text: lastSaved ? `Saved ${formatLastSaved()}` : 'Saved',
          className: 'text-green-600'
        }
      case 'error':
        return {
          icon: <AlertCircle className="w-3 h-3" />,
          text: 'Save failed',
          className: 'text-red-600'
        }
      default:
        return null
    }
  }

  const statusDisplay = getSaveStatusDisplay()

  if (!statusDisplay) {
    return null
  }

  return (
    <div className={cn(
      'flex items-center gap-1.5 text-xs transition-opacity',
      statusDisplay.className,
      className
    )}>
      {statusDisplay.icon}
      <span>{statusDisplay.text}</span>
    </div>
  )
})